/**
 * Focus `element` only when nothing meaningful owns focus — `document.activeElement` fallen back
 * to `<body>` (or nothing) is the state an unmounting panel or control row leaves behind when it
 * takes the focused element with it. Any other active element means the user (or another handler)
 * owns focus, and stealing it would yank the caret out of whatever they are doing.
 *
 * The single definition of "focus is orphaned" — shared by `IconMenuItemsComponent`'s
 * drawer-close reclaim, `WidgetFlowAreaComponent`'s enter-edit park and
 * `WidgetKeyboardLayoutDirective`'s post-reorder repair, so a future refinement (shadow DOM,
 * focus traps) lands everywhere at once instead of silently diverging.
 *
 * `options` passes straight to `focus()` — the keyboard-reorder repair needs `preventScroll`
 * because the repaired card can sit outside the viewport after a move.
 */
export function focusIfOrphaned(element: HTMLElement | null | undefined, options?: FocusOptions): void {
  const active = document.activeElement;
  // A stale reference to a DETACHED node also counts as orphaned. Chromium drops activeElement
  // to <body> the moment the focused node is removed (the behavior the keyboard-reorder repair
  // was verified against), but WebKit has been observed to leave it pointing at the removed node
  // for a tick — and a disconnected element is nobody's focus owner.
  if (active && active !== document.body && active.isConnected) return;
  element?.focus(options);
}
